import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { styled } from '@mui/system'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import MuiDialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import ClearIcon from '@mui/icons-material/ClearRounded'
import AddIcon from '@mui/icons-material/AddRounded'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import TextInput from '../../../../../components/editors/input/TextInput'
import { Button } from '../../../../../components/buttons'
import { generateUuid } from '../../../../../../utils/uuids'
import { WbsItemAdditionalPropertySelectOption } from '../../../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { intl } from '../../../../../../i18n'
import Tooltip from '../../../../../components/tooltips/Tooltip'
import { colorPalette } from '../../../../../style/colorPalette'
import ConfirmDialog, {
  useConfirmDialog,
} from '../../../../../pages/WbsItem/components/dialog/ConfirmDialog'
import DndComponent from '../../../../../components/kanban/DndComponent'
import { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Box from '@mui/material/Box'
import { IDndDraggable } from '../../../../../components/kanban/DndSortableMarker'
import { DragHandle } from '../../../../../components/kanban/items/DragHandle'

type Props = {
  open: boolean
  inputItems: WbsItemAdditionalPropertySelectOption[]
  onSubmit: (newSelectOptions: WbsItemAdditionalPropertySelectOption[]) => void
  onClose: () => void
} & WrappedComponentProps

type DraggableSelectOption = IDndDraggable &
  WbsItemAdditionalPropertySelectOption
type Contents = {
  [uuid: string]: string
}
const createEmptySelectOption = (
  displayOrder: number
): DraggableSelectOption => {
  const uuid = generateUuid()
  return {
    id: uuid,
    uuid,
    selectOption: '',
    displayOrder,
  }
}
export const MultiInputDialog = injectIntl(
  ({ open, inputItems, onSubmit: _onSubmit, onClose, intl }: Props) => {
    const [items, setItems] = useState<DraggableSelectOption[]>([])
    const { openConfirmDialog, confirmDialogProps } = useConfirmDialog()
    const [itemContents, setItemContents] = useState<{
      [uuid: string]: string
    }>({})
    useEffect(() => {
      if (inputItems.length === 0) {
        const emptySelectOption = createEmptySelectOption(1)
        setItems([emptySelectOption])
        setItemContents({
          [emptySelectOption.uuid]: emptySelectOption.selectOption,
        })
      } else {
        const tmpItems: DraggableSelectOption[] = []
        const tmpContents: Contents = {}
        inputItems.forEach(item => {
          tmpItems.push({ id: item.uuid, ...item })
          tmpContents[item.uuid] = item.selectOption
        })
        setItems(tmpItems)
        setItemContents(tmpContents)
      }
    }, [inputItems])
    const onSubmit = useCallback(() => {
      _onSubmit(
        items.map((item, index) => ({
          uuid: item.uuid,
          selectOption: itemContents[item.uuid],
          displayOrder: index + 1,
        }))
      )
    }, [_onSubmit, itemContents, items])
    const onChangeInputItem = useCallback((uuid, selectOption) => {
      setItemContents(contents => ({
        ...contents,
        [uuid]: selectOption,
      }))
    }, [])
    const addInputItem = useCallback(() => {
      setItems(items => [...items, createEmptySelectOption(items.length)])
    }, [])
    const deleteInputItem = useCallback(uuid => {
      openConfirmDialog({
        title: intl.formatMessage({
          id: 'wbsItemAdditionalProperties.cellEditor.selectOptions.dialog.delete.confirm.title',
        }),
        message: intl.formatMessage({
          id: 'wbsItemAdditionalProperties.cellEditor.selectOptions.dialog.delete.confirm.message',
        }),
        confirm: () => {
          setItems(items => {
            return items.filter(item => item.uuid !== uuid)
          })
        },
      })
    }, [])
    const hasError = useMemo(
      () => items.some(v => !itemContents[v.uuid]),
      [items, itemContents]
    )

    const handleDragEnd = useCallback((event: DragEndEvent) => {
      const { active, over } = event
      if (over == null) {
        return
      }
      if (active.id !== over.id) {
        setItems(items => {
          const oldIndex = items.findIndex(item => item.uuid === active.id)
          const newIndex = items.findIndex(item => item.uuid === over.id)
          return arrayMove(items, oldIndex, newIndex)
        })
      }
    }, [])

    return (
      <>
        <Dialog open={open} onClose={onClose}>
          <DialogTitle>
            {intl.formatMessage({
              id: 'wbsItemAdditionalProperties.cellEditor.selectOptions.dialog.title',
            })}
          </DialogTitle>
          <DialogContent>
            <SelectOptionsContainer>
              <DndComponent onDragEnd={handleDragEnd}>
                <SortableContext
                  id={'sortable-select-options'}
                  items={items}
                  strategy={verticalListSortingStrategy}
                >
                  {items.map(item => (
                    <SelectOptionEditor
                      key={item.uuid}
                      id={item.uuid}
                      uuid={item.uuid}
                      selectOption={itemContents[item.uuid]}
                      disabled={!!item.using}
                      onChangeInputItem={onChangeInputItem}
                      deleteInputItem={deleteInputItem}
                    />
                  ))}
                </SortableContext>
              </DndComponent>
            </SelectOptionsContainer>
            <Footer>
              <AddIcon color="action" onClick={addInputItem} />
              {hasError && (
                <FooterMessage>
                  {intl.formatMessage({
                    id: 'wbsItemAdditionalProperties.cellEditor.selectOptions.dialog.selectOption.emptyMessage',
                  })}
                </FooterMessage>
              )}
            </Footer>
          </DialogContent>
          <DialogActions>
            <SubmitButton
              label={intl.formatMessage({ id: 'dialog.ok' })}
              disabled={hasError}
              onClick={onSubmit}
            />
            <CloseButton
              label={intl.formatMessage({ id: 'dialog.close' })}
              disabled={false}
              onClick={onClose}
            />
          </DialogActions>
        </Dialog>
        <ConfirmDialog {...confirmDialogProps} />
      </>
    )
  }
)
const SubmitButton = ({
  label,
  disabled,
  onClick,
}: {
  label: string
  disabled: boolean
  onClick: () => void
}) => {
  return (
    <StyledButton
      variant="filled"
      colorPattern="skyBlue"
      size="s"
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </StyledButton>
  )
}
const CloseButton = ({
  label,
  disabled,
  onClick,
}: {
  label: string
  disabled: boolean
  onClick: () => void
}) => {
  return (
    <StyledButton
      variant="outlined"
      colorPattern="monotone"
      size="s"
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </StyledButton>
  )
}
// Styles
const StyledButton = styled(Button)({
  width: '100px',
})
const DialogContent = styled(MuiDialogContent)({
  width: '510px',
  height: '400px',
})
const Footer = styled('div')({
  paddingTop: 10,
  paddingLeft: 10,
  display: 'flex',
  alignItems: 'center',
})
const FooterMessage = styled('div')({
  color: 'red',
  marginLeft: 20,
})

const SelectOptionsContainer = styled('div')({
  width: '100%',
  maxHeight: '90%',
  overflow: 'scroll',
})

const SelectOptionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '8px 16px',
  width: '100%',
})

const SelectOptionInput = ({
  uuid,
  selectOption,
  onChangeInputItem,
}: {
  uuid: string
  selectOption: string
  onChangeInputItem: (uuid: string, newSelectOption: string) => void
}) => {
  return (
    <Box style={{ flexGrow: 1 }}>
      <TextInput
        id={uuid}
        variant="outlined"
        size="small"
        fullWidth={true}
        value={selectOption}
        onChange={e => onChangeInputItem(uuid, e.target.value)}
      />
    </Box>
  )
}
const SelectOptionEditor = React.memo(
  ({
    id,
    uuid,
    selectOption,
    disabled,
    onChangeInputItem,
    deleteInputItem,
    isDragOverlay,
  }: {
    id: string
    uuid: string
    selectOption: string
    disabled: boolean
    onChangeInputItem: (uuid: string, newSelectOption: string) => void
    deleteInputItem: (uuid: string) => void
    isDragOverlay?: boolean
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
      setActivatorNodeRef,
    } = useSortable({
      id,
    })

    return (
      <SelectOptionRow
        id={uuid}
        key={uuid}
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition: transition,
          opacity: isDragging ? 0.5 : 1,
          cursor: isDragOverlay ? 'grabbing' : 'default',
        }}
        {...attributes}
      >
        <DragHandle
          isDragOverlay={!!isDragOverlay}
          setActivatorNodeRef={setActivatorNodeRef}
          {...listeners}
        />
        <SelectOptionInput
          uuid={uuid}
          selectOption={selectOption}
          onChangeInputItem={onChangeInputItem}
        />
        <Box style={{ width: 10 }}>
          <Tooltip
            message={
              disabled
                ? intl.formatMessage({
                    id: 'wbsItemAdditionalProperties.tooltip.selectOption.canot.delete.in.use',
                  })
                : ''
            }
          >
            <ClearIcon
              color="action"
              style={{
                backgroundColor: disabled
                  ? colorPalette.monotone[2]
                  : 'transparent',
                color: disabled ? colorPalette.monotone[4] : undefined,
                borderRadius: '50%',
                cursor: disabled ? 'default' : 'pointer',
              }}
              onClick={disabled ? undefined : e => deleteInputItem(uuid)}
            />
          </Tooltip>{' '}
        </Box>
      </SelectOptionRow>
    )
  },
  (prev, next) =>
    prev.selectOption === next.selectOption &&
    prev.isDragOverlay === next.isDragOverlay
)
